import React, { useEffect, useState } from "react";
import SEOHead from "../components/shared/SEOHead";
import { getPageSEO } from "../utils/seoConfig";
import { apiConfig } from "../utils/apiConfig";
import ApiEndpoint from "../components/apiRefrencePage/ApiEndPoint";
import Overview from "../components/apiRefrencePage/Overview";
import ParameterBehavior from "../components/apiRefrencePage/ParameterBehavior";
import ErrorHandling from "../components/apiRefrencePage/ErrorHandling";
import SidebarNav from "../components/apiRefrencePage/SideNavbar";
import ApiPricing from "../components/apiRefrencePage/pricing"; // if your file is ApiPricing.tsx, change path accordingly
import { FaBars, FaTimes } from "react-icons/fa";
import zynthtext from "../assets/zynth-text.png";

const ApiPage: React.FC = () => {
  const { endpoints, support } = apiConfig;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>("intro");

  const toggleDrawer = () => setIsDrawerOpen((s) => !s);

  const getAllSectionIds = () => {
    const sectionIds: string[] = ["overview", "getting-started", "api-reference"];

    endpoints.forEach((endpoint: any) => {
      sectionIds.push(endpoint.id);
      if (endpoint.headers?.length) sectionIds.push(`${endpoint.id}-headers`);
      if (endpoint.bodyParams?.length) sectionIds.push(`${endpoint.id}-body-parameters`);
      if (endpoint.method && endpoint.request) sectionIds.push(`${endpoint.id}-request-body`);
      if (endpoint.response) sectionIds.push(`${endpoint.id}-response-body`);
      if (endpoint.modes?.length) {
        endpoint.modes.forEach((mode: any) => {
          if (mode.request) sectionIds.push(`${endpoint.id}-${mode.id}-request-body`);
          if (mode.response) sectionIds.push(`${endpoint.id}-${mode.id}-response-body`);
        });
      }
      sectionIds.push(`${endpoint.id}-examples`);
    });

    sectionIds.push("parameter-behavior");
    sectionIds.push("parameter-library");
    sectionIds.push("presentation-id-behavior");
    sectionIds.push("error-handling");
    sectionIds.push("api-pricing"); // ensure pricing gets observed
    sectionIds.push("support");
    return sectionIds;
  };

  useEffect(() => {
    const sectionIds = getAllSectionIds();
    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean) as Element[];

    const observer = new IntersectionObserver(
      (entries) => {
        let maxRatio = 0;
        let mostVisible = "";
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio;
            mostVisible = (entry.target as HTMLElement).id;
          }
        }
        if (mostVisible) setActiveSection(mostVisible);
      },
      {
        rootMargin: "-20% 0px -70% 0px",
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
      }
    );

    sections.forEach((s) => observer.observe(s));
    return () => sections.forEach((s) => observer.unobserve(s));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoints]);

  return (
    <>
      <SEOHead seo={getPageSEO("apiReference")} />
      <div className="bg-white" style={{ scrollBehavior: "smooth" }}>
      <button
        onClick={toggleDrawer}
        className="lg:hidden fixed top-4 right-4 z-50 p-2 bg-white border border-slate-300 rounded-md shadow-md"
        aria-label="Toggle navigation"
      >
        {isDrawerOpen ? <FaTimes className="h-6 w-6" /> : <FaBars className="h-6 w-6" />}
      </button>

      {isDrawerOpen && (
        <div
          onClick={toggleDrawer}
          className="lg:hidden fixed inset-0 bg-black/30 z-30"
          aria-hidden="true"
        />
      )}

      <div className="flex min-h-screen">
        <aside
          className={`fixed top-0 left-0 h-full w-64 bg-white z-40 flex flex-col transform transition-transform duration-300 ease-in-out
                      lg:sticky lg:top-0 lg:h-screen lg:w-64 lg:translate-x-0 lg:border-r lg:border-slate-200
                      ${isDrawerOpen ? "translate-x-0" : "-translate-x-full"}`}
        >
          <div className="flex-shrink-0 p-8 pb-4">
            <div className="text-xl font-bold text-gray-800">
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-all duration-300 transform hover:scale-110 active:scale-95 active:opacity-80"
              >
                <img className="h-8" src={zynthtext} alt="Zynth" />
              </a>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-8 pb-8 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
            <SidebarNav
              onLinkClick={isDrawerOpen ? toggleDrawer : undefined}
              activeSection={activeSection}
            />
          </div>
        </aside>

        <main className="flex-1 px-4 py-8 lg:ml-20 max-w-full overflow-x-hidden">
          <Overview />

          <section id="getting-started" className="mt-12 pt-8 border-t border-slate-200 scroll-mt-20">
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Getting Started</h2>
            <div className="mt-6 prose prose-slate">
              <h3 className="text-lg font-semibold mt-6 text-slate-700">Getting an API Key</h3>
              <p className="text-slate-600 mb-4">
                Before making your first request, create and copy your <strong>Zynth.ai</strong> API key and{" "}
                <strong>org ID</strong> from your developer dashboard.
              </p>
              <button
                className="flex w-[15%] items-center justify-center my-3 border rounded-md font-semibold text-white bg-[#3667B2] hover:bg-white hover:text-[#3667B2] hover:border hover:border-[#3667B2] active:scale-95 transition transform duration-300 py-2"
                onClick={() => window.open("https://developer.zynth.ai", "_blank")}
              >
                Get API key
              </button>

              <h3 className="text-lg font-semibold mt-8 text-slate-700">Authentication</h3>
              <p className="text-slate-600 mb-4">All requests must include an API key in the header.</p>
              <div className="bg-[#3667B2] rounded-lg p-4 text-sm text-slate-200 font-mono mt-4">
                Authorization: Bearer YOUR_API_KEY
              </div>
            </div>
          </section>

          <section id="api-reference" className="mt-12 pt-8 border-t border-slate-200 scroll-mt-20 w-full max-w-full">
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">API Reference</h2>
            <p className="mt-4 text-slate-600">Detailed documentation for all available API endpoints.</p>
            {endpoints.map((endpoint: any) => (
              <ApiEndpoint key={endpoint.id} endpoint={endpoint} />
            ))}
          </section>

          <ParameterBehavior />
          <ErrorHandling />
          <section id="api-pricing" className="mt-12 pt-8 border-t border-slate-200 scroll-mt-20">
            <ApiPricing />
          </section>

          <section id="support" className="mt-12 pt-8 border-t border-slate-200 scroll-mt-20">
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Support</h2>
            <p className="mt-4 text-slate-600">
              For help, contact{" "}
              <a href={`mailto:${support.email}`} className="text-blue-600 hover:underline">
                {support.email}
              </a>
              .
            </p>
          </section>
        </main>
      </div>
      </div>
    </>
  );
};

export default ApiPage;
